import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const InputContainer = styled.div`
  display: flex;
  padding: 16px 24px;
  background-color: white;
  border-top: 1px solid #e0e0e0;
  align-items: center;
`;

const TextInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: #4a6fa5;
    box-shadow: 0 0 0 2px rgba(74, 111, 165, 0.2);
  }
`;

const SendButton = styled.button`
  background-color: #4a6fa5;
  color: white;
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  margin-left: 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #3a5c8a;
    transform: scale(1.05);
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #4a6fa5;
  margin: 0 8px;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    color: #3a5c8a;
    transform: scale(1.1);
  }
`;

const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white"/>
  </svg>
);

const EmojiIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM15.5 11C16.33 11 17 10.33 17 9.5C17 8.67 16.33 8 15.5 8C14.67 8 14 8.67 14 9.5C14 10.33 14.67 11 15.5 11ZM8.5 11C9.33 11 10 10.33 10 9.5C10 8.67 9.33 8 8.5 8C7.67 8 7 8.67 7 9.5C7 10.33 7.67 11 8.5 11ZM12 17.5C14.33 17.5 16.31 16.04 17.11 14H6.89C7.69 16.04 9.67 17.5 12 17.5Z" />
  </svg>
);

const MessageInput = ({ onSendMessage, onStartTyping }) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const hasTypedRef = useRef(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const handleChange = (e) => {
    const newMessage = e.target.value;
    setMessage(newMessage);
    
    // Notify parent of first keystroke only
    if (!hasTypedRef.current && newMessage.length > 0) {
      hasTypedRef.current = true;
      onStartTyping?.();
    }
  };

  const addEmoji = (emoji) => {
    setMessage(prev => prev + emoji);
    inputRef.current.focus();
    if (!hasTypedRef.current) {
      hasTypedRef.current = true;
      onStartTyping?.();
    }
  };

  // Focus input on component mount
  useEffect(() => {
    inputRef.current.focus();
  }, []);

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <InputContainer>
        <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
          <ActionButton 
            type="button" 
            onClick={() => addEmoji('😊')}
            title="Add smile emoji"
          >
            <EmojiIcon />
          </ActionButton>
          <TextInput
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          <SendButton 
            type="submit" 
            disabled={!message.trim()}
            title="Send message"
          >
            <SendIcon />
          </SendButton>
        </div>
      </InputContainer>
    </form>
  );
};

export default MessageInput;
